/* ==========================================================================
   Shopfly Core JS v3
   - Scroll-reveal for [data-sf-anim] AND legacy [data-sf-reveal]
   - Parent stagger via [data-sf-anim-stagger]
   - Per-element delay via [data-sf-anim-delay]
   - Header scroll state
   - Mobile nav
   - Pricing toggle with animated price swap + selling plan wiring
   - FAQ spring accordion
   - Parallax
   - Counter animation
   ========================================================================== */

(function () {
  'use strict';

  /* -------------------------------------------------------------------- */
  /*  SCROLL-REVEAL                                                        */
  /*  Observes [data-sf-anim] and [data-sf-reveal] elements.               */
  /*  Adds .sf-visible (new) or .is-visible (legacy) when in viewport.     */
  /*  Supports stagger via parent [data-sf-anim-stagger="120"].            */
  /*  Supports per-element delay via [data-sf-anim-delay="200"].           */
  /* -------------------------------------------------------------------- */

  var observer = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      var el = entries[i].target;

      // Stagger parent — animate children sequentially
      if (el.hasAttribute('data-sf-anim-stagger')) {
        revealStaggerChildren(el);
      } else {
        revealElement(el);
      }

      // Unobserve unless repeat mode
      if (el.getAttribute('data-sf-anim-once') !== 'false') {
        observer.unobserve(el);
      }
    }
  }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });

  function revealElement(el) {
    var delay = parseInt(el.getAttribute('data-sf-anim-delay'), 10) || 0;
    // Legacy delay support
    var legacyDelay = parseInt(el.getAttribute('data-sf-reveal-delay'), 10);
    if (legacyDelay) delay = legacyDelay * 100;

    if (delay > 0) {
      el.style.transitionDelay = delay + 'ms';
    }

    // Use requestAnimationFrame for smoother paint
    requestAnimationFrame(function () {
      el.classList.add('sf-visible');
      el.classList.add('is-visible'); // legacy compat
    });
  }

  function revealStaggerChildren(parent) {
    var gap = parseInt(parent.getAttribute('data-sf-anim-stagger'), 10) || 120;
    var children = parent.querySelectorAll('[data-sf-anim], [data-sf-reveal]');

    // Reveal parent first
    requestAnimationFrame(function () {
      parent.classList.add('sf-visible');
      parent.classList.add('is-visible');
    });

    // Then stagger children
    for (var i = 0; i < children.length; i++) {
      (function (child, index) {
        var childDelay = index * gap;
        child.style.transitionDelay = childDelay + 'ms';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            child.classList.add('sf-visible');
            child.classList.add('is-visible');
          });
        });
      })(children[i], i);
    }
  }

  function initReveal(root) {
    root = root || document;

    // Observe stagger parents
    var staggerParents = root.querySelectorAll('[data-sf-anim-stagger]');
    for (var i = 0; i < staggerParents.length; i++) {
      observer.observe(staggerParents[i]);
    }

    // Observe individual elements not inside a stagger parent
    var items = root.querySelectorAll('[data-sf-anim], [data-sf-reveal]');
    for (var j = 0; j < items.length; j++) {
      if (!items[j].closest('[data-sf-anim-stagger]')) {
        observer.observe(items[j]);
      }
    }
  }


  /* -------------------------------------------------------------------- */
  /*  HEADER                                                               */
  /* -------------------------------------------------------------------- */

  function initHeader() {
    var header = document.querySelector('[data-sf-header]');
    if (!header) return;
    var ticking = false;
    function update() {
      if (!ticking) {
        requestAnimationFrame(function () {
          header.classList.toggle('is-scrolled', window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
  }


  /* -------------------------------------------------------------------- */
  /*  MOBILE NAV                                                           */
  /* -------------------------------------------------------------------- */

  function initMobileNav() {
    var toggle = document.querySelector('[data-sf-nav-toggle]');
    var nav = document.querySelector('[data-sf-mobile-nav]');
    if (!toggle || !nav) return;
    toggle.addEventListener('click', function () {
      var isOpen = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(isOpen));
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });
    nav.addEventListener('click', function (e) {
      if (e.target.tagName === 'A') {
        nav.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }
    });
  }


  /* -------------------------------------------------------------------- */
  /*  PRICING TOGGLE                                                       */
  /* -------------------------------------------------------------------- */

  function initPricingToggle() {
    var toggles = document.querySelectorAll('[data-sf-pricing-toggle]');
    for (var t = 0; t < toggles.length; t++) {
      (function (toggleRoot) {
        var buttons = toggleRoot.querySelectorAll('[data-sf-billing]');
        var section = toggleRoot.closest('[data-sf-pricing-section]');
        if (!section) return;

        for (var b = 0; b < buttons.length; b++) {
          buttons[b].addEventListener('click', function () {
            var mode = this.getAttribute('data-sf-billing');
            for (var k = 0; k < buttons.length; k++) {
              buttons[k].classList.toggle('is-active', buttons[k] === this);
            }
            section.setAttribute('data-sf-billing-mode', mode);

            // Animated price swap
            var prices = section.querySelectorAll('[data-sf-price-monthly]');
            for (var p = 0; p < prices.length; p++) {
              (function (priceEl) {
                var monthly = priceEl.getAttribute('data-sf-price-monthly');
                var yearly = priceEl.getAttribute('data-sf-price-yearly');
                var amountEl = priceEl.querySelector('[data-sf-price-amount]');
                if (!amountEl) return;

                amountEl.style.transition = 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.15s ease';
                amountEl.style.transform = 'scale(0.8)';
                amountEl.style.opacity = '0.3';

                setTimeout(function () {
                  amountEl.textContent = mode === 'yearly' ? yearly : monthly;
                  amountEl.style.transform = 'scale(1)';
                  amountEl.style.opacity = '1';
                }, 160);
              })(prices[p]);
            }

            // Swap selling plans
            var forms = section.querySelectorAll('[data-sf-selling-plan-monthly]');
            for (var f = 0; f < forms.length; f++) {
              var input = forms[f].querySelector('input[name="selling_plan"]');
              if (input) {
                input.value = mode === 'yearly'
                  ? forms[f].getAttribute('data-sf-selling-plan-yearly')
                  : forms[f].getAttribute('data-sf-selling-plan-monthly');
              }
            }
          });
        }
      })(toggles[t]);
    }
  }


  /* -------------------------------------------------------------------- */
  /*  FAQ ACCORDION                                                        */
  /* -------------------------------------------------------------------- */

  function initFaq() {
    var items = document.querySelectorAll('[data-sf-faq-item]');
    for (var i = 0; i < items.length; i++) {
      (function (item) {
        var trigger = item.querySelector('[data-sf-faq-trigger]');
        var panel = item.querySelector('[data-sf-faq-panel]');
        if (!trigger || !panel) return;

        panel.style.transition = 'max-height 0.5s cubic-bezier(0.25, 1, 0.5, 1)';

        trigger.addEventListener('click', function () {
          var isOpen = item.classList.toggle('is-open');
          trigger.setAttribute('aria-expanded', String(isOpen));

          if (isOpen) {
            var parent = item.closest('[data-sf-section="faq"]');
            if (parent && parent.hasAttribute('data-sf-faq-single')) {
              var siblings = parent.querySelectorAll('[data-sf-faq-item].is-open');
              for (var s = 0; s < siblings.length; s++) {
                if (siblings[s] !== item) {
                  siblings[s].classList.remove('is-open');
                  var st = siblings[s].querySelector('[data-sf-faq-trigger]');
                  var sp = siblings[s].querySelector('[data-sf-faq-panel]');
                  if (st) st.setAttribute('aria-expanded', 'false');
                  if (sp) sp.style.maxHeight = '0';
                }
              }
            }
            panel.style.maxHeight = panel.scrollHeight + 'px';
          } else {
            panel.style.maxHeight = '0';
          }
        });
      })(items[i]);
    }
  }


  /* -------------------------------------------------------------------- */
  /*  PARALLAX                                                             */
  /* -------------------------------------------------------------------- */

  function initParallax() {
    var els = document.querySelectorAll('[data-sf-parallax]');
    if (!els.length) return;
    var ticking = false;
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(function () {
          var scrollY = window.scrollY;
          for (var i = 0; i < els.length; i++) {
            var rect = els[i].getBoundingClientRect();
            if (rect.bottom > 0 && rect.top < window.innerHeight) {
              var speed = parseFloat(els[i].getAttribute('data-sf-parallax')) || 0.06;
              els[i].style.transform = 'translate3d(0,' + (scrollY * speed) + 'px,0)';
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }


  /* -------------------------------------------------------------------- */
  /*  COUNTER                                                              */
  /* -------------------------------------------------------------------- */

  function initCounters() {
    var cObs = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          animateCounter(entries[i].target);
          cObs.unobserve(entries[i].target);
        }
      }
    }, { threshold: 0.3 });

    var counters = document.querySelectorAll('[data-sf-counter]');
    for (var i = 0; i < counters.length; i++) cObs.observe(counters[i]);
  }

  function animateCounter(el) {
    var target = parseFloat(el.getAttribute('data-sf-counter'));
    var suffix = el.getAttribute('data-sf-counter-suffix') || '';
    var prefix = el.getAttribute('data-sf-counter-prefix') || '';
    var dur = parseInt(el.getAttribute('data-sf-counter-duration'), 10) || 1200;
    var start = null;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + Math.round(target * eased).toLocaleString() + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }


  /* -------------------------------------------------------------------- */
  /*  INIT                                                                 */
  /* -------------------------------------------------------------------- */

  function init() {
    initReveal();
    initHeader();
    initMobileNav();
    initPricingToggle();
    initFaq();
    initParallax();
    initCounters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.SHOPFLY = window.SHOPFLY || {};
  window.SHOPFLY.initReveal = initReveal;
  window.SHOPFLY.initFaq = initFaq;
  window.SHOPFLY.initPricingToggle = initPricingToggle;
  window.SHOPFLY.initCounters = initCounters;

  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    initFaq();
    initPricingToggle();
    initCounters();
  });
})();
